import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Mail, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const EMAIL_RE = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/;

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

interface Props {
  docId:       string;
  clientEmail: string | null;
  refreshKey?: number;
}

export function TranzilaEmailButton({ docId, clientEmail, refreshKey }: Props) {
  const [sentAt,  setSentAt]  = useState<string | null>(null);
  const [sentTo,  setSentTo]  = useState<string | null>(null);
  const [open,    setOpen]    = useState(false);
  const [to,      setTo]      = useState('');
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('external_invoice_pushes')
      .select('email_to, email_sent_at')
      .eq('document_id', docId)
      .eq('provider', 'tranzila')
      .eq('status', 'success')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error: e }) => {
        if (cancelled || e || !data) return;
        setSentAt(data.email_sent_at ?? null);
        setSentTo(data.email_to ?? null);
      });
    return () => { cancelled = true; };
  }, [docId, refreshKey]);

  const openModal = () => { setTo(sentTo ?? clientEmail ?? ''); setError(null); setOpen(true); };
  const close = () => { if (!sending) setOpen(false); };

  const send = async () => {
    const addr = to.trim();
    if (!EMAIL_RE.test(addr)) { setError('כתובת המייל אינה תקינה'); return; }
    setSending(true); setError(null);
    const { data, error: e } = await supabase.functions.invoke('external-invoice', {
      body: { action: 'resend_email', provider: 'tranzila', document_id: docId, email_to: addr },
    });
    setSending(false);
    if (data?.ok === true && typeof data.email_sent_at === 'string') {
      setSentAt(data.email_sent_at);
      setSentTo(typeof data.email_to === 'string' ? data.email_to : addr);
      setOpen(false);
      return;
    }
    setError(typeof data?.error === 'string' ? data.error : e ? 'לא הצלחנו להתחבר לשרת. נסו שוב בעוד רגע.' : 'השליחה נכשלה. נסו שוב.');
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        title={sentAt ? `נשלח במייל ל-${sentTo ?? ''} ב-${fmtDateTime(sentAt)}` : 'שלח את המסמך ללקוח במייל'}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
          sentAt ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
        }`}
      >
        {sentAt ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
        {sentAt ? 'נשלח במייל · שלח שוב' : 'שלח במייל'}
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" dir="rtl" onClick={close}>
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">שליחת המסמך במייל</h2>
              <button onClick={close} disabled={sending} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              {sentAt && (
                <p className="text-xs text-gray-500">נשלח לאחרונה ל-{sentTo} ב-{fmtDateTime(sentAt)}</p>
              )}
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1.5">כתובת מייל</span>
                <input type="email" dir="ltr" value={to} onChange={e => setTo(e.target.value)} autoFocus
                       className="w-full px-3 py-2 text-sm text-left border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400" />
              </label>
              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{error}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={close} disabled={sending} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
                ביטול
              </button>
              <button onClick={send} disabled={sending}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                {sending ? <><Loader2 className="w-4 h-4 animate-spin" />שולח…</> : <><Mail className="w-4 h-4" />שלח</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
